import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { FiCalendar, FiMapPin, FiClock, FiShoppingBag, FiTruck, FiMusic, FiSmile, FiExternalLink } from "react-icons/fi";
import EventVendors from "./EventVendors";
import "./eventpage.css";

const TABS = [
  { id: "overview", label: "OVERVIEW" },
  { id: "vendors", label: "CURATED VENDORS" },
];

const GlobalVillage2026 = () => {
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <>
      <Helmet>
        <title>Bizarre Bazaar Global Village Festival 2026 | Sept 26–27, Gigiri | Biz Baz Events</title>
        <meta
          name="description"
          content="Join Bizarre Bazaar Global Village Festival, September 26–27 2026 at KSTVET, Gigiri. 200+ vendors, food court, live music, kids zone & DJ sets."
        />
        <meta property="og:title" content="Bizarre Bazaar Global Village Festival 2026" />
        <meta property="og:description" content="200+ vendors, live music, food & culture — September 26–27, 2026 at KSTVET, Gigiri." />
        <meta property="og:image" content="/images/posters/2024/Global_village_edition_2026.png" />
      </Helmet>

      <div className="eventpage-container">
        
        {/* HERO BANNER */}
        <div 
          className="event-hero" 
          style={{ backgroundImage: "url('/images/REM00008.jpg')" }}
        >
          <div className="event-hero-overlay">
            <div className="hero-content-inner">
              <span className="hero-category-tag">BIZARRE BAZAAR FESTIVAL</span>
              <h1 className="hero-event-title">THE GLOBAL VILLAGE EDITION</h1>
              
              <div className="hero-meta-strip">
                <div className="meta-item">
                  <FiCalendar className="meta-icon" />
                  <span>Sept 26th &amp; 27th, 2026</span>
                </div>
                <div className="meta-item">
                  <FiMapPin className="meta-icon" />
                  <span>KSTVET, UN Avenue, Gigiri</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* NAVIGATION TABS */}
        <div className="event-tabbar-wrap">
          <nav className="event-tabbar" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`event-tab ${activeTab === tab.id ? "event-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* TWO COLUMN LAYOUT */}
        <div className="event-body-layout">
          
          {/* MAIN TAB CONTENT */}
          <main className="event-body-main">
            {activeTab === "overview" && (
              <div id="event-panel-overview" role="tabpanel">
                
                <div className="ep-intro-box">
                  <p className="ep-intro">
                    <strong>Bizarre Bazaar Festival: The Global Village Edition</strong> brings the world to one weekend — culture, creativity, food, music, shopping, and connection.
                  </p>
                  <p>
                    Step into an open-air marketplace built by makers, artisans, designers, creative entrepreneurs, food vendors, and independent brands from Kenya and beyond.
                  </p>
                  <p>
                    Discover fashion and accessories, art, home and lifestyle finds, handmade goods, gifts, food, drink, and brands on the rise.
                  </p>
                </div>

                {/* HIGHLIGHTS */}
                <div className="ep-highlights-grid">
                  <div className="highlight-card">
                    <FiShoppingBag className="hl-icon" />
                    <h4>200+ Vendors</h4>
                    <p>Fashion, art, handmade crafts, gifts &amp; local artisan products.</p>
                  </div>
                  <div className="highlight-card">
                    <FiTruck className="hl-icon" />
                    <h4>Gourmet Food Court</h4>
                    <p>Global street food, artisan coffee, and craft drinks.</p>
                  </div>
                  <div className="highlight-card">
                    <FiMusic className="hl-icon" />
                    <h4>Live Music &amp; DJs</h4>
                    <p>Live acoustic sets and evening DJ performances.</p>
                  </div>
                  <div className="highlight-card">
                    <FiSmile className="hl-icon" />
                    <h4>Family &amp; Dog Friendly</h4>
                    <p>Kids activity village &amp; dog-friendly grounds (on leash).</p>
                  </div>
                </div>

                {/* ACCESS BADGES */}
                <div className="event-access-grid">
                  <div className="event-access-badge eab--blue">
                    <span className="eab-icon">♿</span>
                    <span className="eab-label">Venue Accessible</span>
                  </div>
                  <div className="event-access-badge eab--amber">
                    <span className="eab-icon">👪</span>
                    <span className="eab-label">Family Friendly</span>
                  </div>
                  <div className="event-access-badge eab--green">
                    <span className="eab-icon">🦮</span>
                    <span className="eab-label">Dog Friendly (On Leash)</span>
                  </div>
                </div>

              </div>
            )}

            {activeTab === "vendors" && (
              <div id="event-panel-vendors" role="tabpanel">
                <EventVendors eventId="66489b2f-1fa8-4307-a57d-d62a60dba217" />
              </div>
            )}
          </main>

          {/* STICKY SIDEBAR (POSTER & SINGLE TICKET CARD) */}
          <aside className="event-sidebar">
            
            {/* PRIORITY 1: OFFICIAL POSTER */}
            <div className="sidebar-poster-card">
              <img 
                src="/images/posters/2024/Global_village_edition_2026.png" 
                alt="Global Village Edition 2026 Official Poster" 
                className="event-poster-img"
              />
            </div>

            {/* PRIORITY 2: TICKETS & DETAILS */}
            <div className="sidebar-ticket-card">
              <h3 className="sidebar-title">EVENT TICKETS &amp; DETAILS</h3>
              
              <div className="sidebar-pricing">
                <div className="price-row">
                  <span>Adults (Daily Charge)</span>
                  <strong>Ksh 500</strong>
                </div>
                <div className="price-row">
                  <span>Under 18s (Daily Charge)</span>
                  <strong>Ksh 200</strong>
                </div>
              </div>

              <div className="sidebar-meta">
                <div className="s-meta-item">
                  <FiCalendar className="s-icon" />
                  <span>Sat Sept 26 &amp; Sun Sept 27, 2026</span>
                </div>
                <div className="s-meta-item">
                  <FiClock className="s-icon" />
                  <span>9:00 AM – 7:00 PM Daily</span>
                </div>
                <div className="s-meta-item">
                  <FiMapPin className="s-icon" />
                  <span>KSTVET, UN Avenue, Gigiri</span>
                </div>
              </div>

              <a 
                href="https://kenyabuzz.com/events/event/bizarre-bazaar-festival-the-global-village-edition" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="sidebar-cta-btn"
              >
                GET TICKETS ON KENYABUZZ <FiExternalLink />
              </a>

              <p className="sidebar-note">Tickets available in advance online or at the door via M-Pesa &amp; Cash.</p>
            </div>

          </aside>

        </div>
      </div>
    </>
  );
};

export default GlobalVillage2026;